import Head from 'next/head';
import {useRouter} from 'next/router';

import {GwgAccelerator} from '@udemy/career-academies';
import {HeadSEO, useCanonicalUrl} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {META_DEFAULT_BASE_URL} from '@udemy/nextjs-core';
import {GwgCheckoutProvider} from '@udemy/subscription-browse';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {getLayout} from 'src/components/layout';
import {useGwgPlanPreference} from 'src/hooks/useGwgPlanPreference';

import {PlanComparisonBody} from './plan-comparison-body.react-component';

export interface PlanComparisonFeatureProps {
    allGwgAccelerators?: GwgAccelerator[];
}

export const PlanComparisonFeature = ({allGwgAccelerators}: PlanComparisonFeatureProps = {}) => {
    const router = useRouter();
    const {url: canonicalUrl} = useCanonicalUrl(
        router as {locale: string; asPath: string},
        [],
        META_DEFAULT_BASE_URL,
    );
    const {gettext} = useI18n();

    const META_PROPS = {
        seoTitle: gettext('Compare Udemy Plans and Pricing | Udemy'),
        seoDescription: gettext(
            'Compare Udemy’s plans and choose the best learning solution for your upskilling needs. From personal to business subscription plans, you can find pricing plans that match your unique goals.',
        ),
    };
    const {Config: udConfig} = useUDData();
    const showUBLA = udConfig && 'US' === udConfig.price_country?.id;
    const planPreference = useGwgPlanPreference();

    return (
        <>
            <HeadSEO
                title={META_PROPS.seoTitle}
                ogTitle={META_PROPS.seoTitle}
                description={META_PROPS.seoDescription}
                ogDescription={META_PROPS.seoDescription}
                ogType={'website'}
                ogLocale={process.env.I18N_PATH_TO_BUNDLE_MAP[router.locale]}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                robots={['index', 'follow']}
                headWrapper={Head}
            />
            <SubscriptionPlansProvider
                subscriptionPlanQueryVariables={[
                    {
                        filters: {productType: SubscriptionPlanProductType.Consumersubscription},
                        marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                    },
                    {filters: {productType: SubscriptionPlanProductType.Team}},
                    {
                        filters: {
                            productType: SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
                        },
                        marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                    },
                    {
                        filters: {productType: SubscriptionPlanProductType.GrowWithGoogle},
                        marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                    },
                    {
                        filters: {
                            productType:
                                SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                        },
                        marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                    },
                ]}
            >
                <GwgCheckoutProvider planPreference={planPreference}>
                    <PlanComparisonBody
                        showUBLA={showUBLA}
                        allGwgAccelerators={allGwgAccelerators}
                    />
                </GwgCheckoutProvider>
            </SubscriptionPlansProvider>
        </>
    );
};

PlanComparisonFeature.getLayout = getLayout;
